import React, { useMemo } from 'react';
import { NavMenu } from 'tea-component';
import '@/pages/home/assets/scss/components/navHeader.scss';
import { connect, useLocation } from 'umi';
import NavList from './components/nav-list';
import Search from './components/search';
import Help from './components/help';
import Locale from './components/locale';
import Feedback from './components/Feedback';
import Login from './components/login';
import Logo from './components/logo';
import Aigc from './components/Aigc';
import VersionAutoPopup from '@/pages/home/components/VersionPopup/VersionAutoPopup';
import { getSysType, sysTypeOrEnvType } from '@/pages/home/components/VersionPopup/_config';
import { navListConfig, defaultOptions, defaultHelpList, defaultHelpUrl, AI_LIST } from './_config';
import { IS_GLOBAL } from '@/pages/home/_config/config';
import aigcRouters from '@/pages/aigc/_config/routers';

import type { navItemType } from './_config';
import { getImg } from '@/pages/home/_config/getImgs';
// import OldVersion from '@/pages/home/components/NavHeader/components/OldVersion';
import Theme from '@/pages/home/components/NavHeader/components/Theme';
import { ORGANIZATION_FUN } from '@/pages/dashboard/_config/constant';
import { INavStudioItem } from '@home/types/home';
import { resolveRedirectPathForMissingHub } from '@/pages/home/pages/studio/utils';
import { isAIGalleryAllowed } from '@/pages/home/_config/aigallery';

/** 生成 studio 导航跳转路径（复用 resolveRedirectPathForMissingHub 的优先级逻辑） */
const getStudioHref = (studio: INavStudioItem): string => {
  const {
    haveHome,
    studio_code: studioCode,
    first_game_code_or_unified_id: gameCode,
    first_game_dashboard_list: dashboardList,
  } = studio || {};

  if (!studioCode) return '/home';

  // 复用 resolveRedirectPathForMissingHub 的完整优先级逻辑：
  //   0. haveHome → /studio/{id}/home
  //   1. studio_dashboard_list 第一个有效 path
  //   2. game_list 第一个游戏的 dashboard_list 第一个有效 path
  //   3. 兜底 /home
  return resolveRedirectPathForMissingHub(
    {
      haveHome,
      studio_dashboard_list: studio?.studio_dashboard_list,
      game_list: gameCode
        ? [{
          game_code: gameCode,
          unified_edition_id: gameCode,
          dashboard_list: dashboardList,
          dashboard_research_list: [],
        }]
        : [],
    },
    studioCode,
  ) || '/home';
};

// import ChatGPT from '@/pages/home/components/NavHeader/components/ChatGPT';

function getNavMenus(list: any, permissions: string[] = []) {
  const sortKeys = ['homepage', 'dashboard', 'intelligence', 'opinion', 'globalpedia', 'benchmark'];
  try {
    const curPermissions = permissions.map((permission) => permission.trim().toLowerCase());
    // 根据配置开放情报、舆情、GlobalPedia系统
    // ['intelligence', 'opinion', 'globalpedia'].forEach(sys => {
    //   if (!SYSTEM_INFO[sys][ENV]) {
    //     curPermissions = curPermissions.filter(val => {
    //       return !val.startsWith(sys);
    //     });
    //   }
    // });
    const filteredList = list.filter((menu: navItemType) => {
      const rbac = menu?.rbac?.toLowerCase() || '';
      if (menu?.name?.toLocaleLowerCase() === "opinions") return true;

      // 有移动端的权限就会有pc的权限
      return curPermissions.some((val) => val === rbac || val === `${rbac}_pc`) || rbac === 'globalpedia';
    });

    // 仅当 intelligence 在菜单中时，才把 benchmark 合并为其下拉子项；
    // 否则保留 benchmark 作为独立项（兼容“只有 benchmark 权限、没有 intelligence”的用户）
    const hasIntelligenceMenu = filteredList.some((menu: navItemType) =>
      (menu?.rbac?.toLowerCase() || '').startsWith('intelligence'),
    );

    const menus = filteredList
      .sort((a: navItemType, b: navItemType) => {
        const rbacA = a?.rbac?.toLowerCase() || '';
        const rbacB = b?.rbac?.toLowerCase() || '';
        const idxA = sortKeys.findIndex((o) => rbacA.indexOf(o) !== -1) || 0;
        const idxB = sortKeys.findIndex((o) => rbacB.indexOf(o) !== -1) || 0;
        return idxA - idxB;
      })
      .map((menu: navItemType) => {
        let newMenu = { ...menu };

        const { rbac } = newMenu;
        // benchmark 已并入 intelligence 下拉时，独立 benchmark 项不再单独展示
        if (rbac.startsWith('benchmark') && hasIntelligenceMenu) {
          return '';
        }

        if (rbac.startsWith('dashboard')) {
          newMenu.children = permissions?.includes('datalab') ? [
            {
              ...newMenu,
              ...navListConfig?.dashboard,
            },
            {
              ...navListConfig?.datalab,
            },
          ] : [
            {
              ...newMenu,
              ...navListConfig?.dashboard,
            },
          ];
        }

        // intelligence 下拉聚合 benchmark（参考 dashboard/datalab 模式）
        if (rbac.startsWith('intelligence')) {
          const hasBenchmarkPerm =
            permissions?.includes('benchmark') || permissions?.includes('benchmark_pc');
          if (hasBenchmarkPerm) {
            newMenu.children = [
              {
                ...newMenu,
                ...navListConfig?.intelligence,
              },
              {
                ...navListConfig?.benchmark,
              },
            ];
          }
        }
        if (rbac.startsWith('globalpedia')) {
          const hasMgmtPermission = permissions?.includes('mgmt_pc');
          if (hasMgmtPermission && permissions?.includes('globalpedia_pc')) {
            const gPop = {
              ...newMenu,
              ...navListConfig?.globalpedia,
            };
            newMenu = { ...navListConfig?.managementdashboard };
            newMenu.children = [
              {
                ...navListConfig?.managementdashboard,
              },
              {
                ...gPop,
              },
            ];
            newMenu.name = 'Mgmt';
            newMenu.title = 'Mgmt';
            return newMenu;
          }
          if (hasMgmtPermission && !permissions?.includes('globalpedia_pc')) {
            newMenu = { ...navListConfig?.managementdashboard };
            newMenu.name = 'Mgmt';
            newMenu.title = 'Mgmt';
            return newMenu;
          }
          if (permissions?.includes('globalpedia_pc')) {
            return { ...newMenu, ...navListConfig[rbac] };
          }
          return '';
        }

        // 接口未返回home的中文字段，暂时前端添加
        if (rbac.startsWith('homepage')) {
          newMenu.title_cn = '首页';
        }

        if (rbac.startsWith("opinion")) {
          const list = [];
          if (permissions.includes("opinion") || permissions?.includes('opinion_pc')) {
            list.push({...newMenu, ...navListConfig?.opinion, href: "/opinion/Overview/KeyMetrics" });
          } 
          if (permissions?.includes('market_pc') || permissions?.includes('market')) {
            const item = { ...navListConfig?.marketing }; 
            list.push(item);
          }

          if (list.length === 0) {
            return null;
          }
          
          if(list.length === 1){
            return list[0];
          }
          
          newMenu.children = list;
          return {...navListConfig.opinion, ...newMenu};
        }
        return {
          ...navListConfig[rbac],
          ...newMenu,
          // icon: navListConfig[rbac].icon,
          // actionIcon: navListConfig[rbac].actionIcon
        };
      });
    return menus.filter((item) => item);
  } catch (err) {
    console.log(err);
    return [];
  }
}

function NavHeader(props: any) {
  const location = useLocation();
  const {
    userInfo,
    navMenuList,
    navMenuListLoaded,
    ifHasSearch = true,
    navStudioList,
    defaultValue = location.pathname,
  } = props;

  const isAIToolClicked: boolean = useMemo(
    () =>
      AI_LIST.some((group) => group.list.some((item) => location.pathname.includes(item?.link)) ||
        aigcRouters.some((route: { path: string; }) => location.pathname.includes(route.path))),
    [location.pathname],
  );

  const sysType = getSysType();

  // 用户权限
  const permissions = useMemo(() => {
    if (userInfo?.permissions) {
      const perms = Object.keys(userInfo.permissions);
      return perms;
    }
    return [];
  }, [userInfo?.permissions]);
  // AI工具权限
  const isHaveAIPermission = useMemo(
    () => permissions.includes('aigc') || permissions.includes('aigc_pc'),
    [permissions],
  );

  // 搜索权限,帮助权限,logo跳转权限,反馈权限
  const { searchPermission, helpPermission, logoJumpPermission, feedbackPermission } = useMemo(() => {
    const searchPermission = permissions?.includes('header_search');
    const helpPermission = permissions?.includes('header_help');
    const logoJumpPermission = permissions?.includes('header_logo_jump');
    const feedbackPermission = permissions?.includes('header_feedback');
    return {
      searchPermission,
      helpPermission,
      logoJumpPermission,
      feedbackPermission,
    };
  }, [permissions]);

  const menus = useMemo(() => {
    if (!navMenuList || navMenuList.length === 0) {
      return [];
    }
    const updatedNavMenuList = [...navMenuList];
    const menus = getNavMenus(updatedNavMenuList, permissions);
    return menus;
  }, [navMenuList, permissions]);

  // studio下拉列表
  const children = useMemo(
    () =>
      navStudioList.map((studio: INavStudioItem) => {
        const { studio_code: studioCode, icon_url: iconUrl, name_en: nameEn, name_cn: nameCn } = studio || {};
        return {
          actionIcon: '',
          icon: 'studio-icon',
          imgIcon: iconUrl,
          href: getStudioHref(studio),
          name: nameEn,
          title_cn: nameCn,
          rbac: studioCode,
          sys: `/studio/${studioCode}`,
          type: 'studio',
        };
      }),
    [navStudioList],
  );

  // studio Menu
  const studioMenu = useMemo(() => {
    const studio = navStudioList?.[0];

    if (menus.length > 0 && navStudioList && navStudioList.length > 0) {
      return {
        actionIcon: 'action-studio-icon',
        icon: 'studio-icon',
        subIcon: 'ai-icon',
        // 点击studio Menu, 默认跳转列表的第一个studio
        href: getStudioHref(studio),
        name: 'Studio Hub',
        rbac: 'studio',
        title: 'studio',
        title_cn: 'Studio专区',
        sys: '/studio',
        children,
      };
    }
    return null;
  }, [children, menus, navStudioList]);

  // AI Gallery 灰度白名单（pro 环境按 openid 命中；其他环境默认全开）
  const isInAIGalleryWhitelist = useMemo(
    () => isAIGalleryAllowed(userInfo?.openid),
    [userInfo?.openid],
  );

  // AI Gallery 入口：依赖 aigc 权限（与 Aigc/AITools 一致） + 灰度白名单
  const aiGalleryMenu = useMemo(
    () =>
      isHaveAIPermission && isInAIGalleryWhitelist
        ? { ...navListConfig.aigallery }
        : null,
    [isHaveAIPermission, isInAIGalleryWhitelist],
  );

  // 头部menuList
  const menuList = useMemo(() => {
    // 必须等 navMenuList 与 userInfo 都就绪后，才统一组装所有菜单项
    // （含 Studio Hub、AI Gallery、GameWiki）
    // - navMenuListLoaded：避免菜单接口未完成时先渲染
    // - userInfo：其它菜单依赖 permissions；若仅等菜单接口，GameWiki（无权限门槛）会单独先露出
    if (!navMenuListLoaded || !userInfo) {
      return [];
    }
    const updatedMenus = [...menus];
    if (studioMenu) {
      // 沿用旧逻辑：把 studio 固定在 benchmark 前面；benchmark 已并入 intelligence 时退化为放到末尾
      const benchmarkIndex = updatedMenus.findIndex((menu) => menu.rbac === 'benchmark');
      if (benchmarkIndex !== -1) {
        updatedMenus.splice(benchmarkIndex, 0, studioMenu);
      } else {
        updatedMenus.push(studioMenu);
      }
    }
    if (aiGalleryMenu) {
      updatedMenus.push(aiGalleryMenu);
    }
    // GameWiki 固定跟在 AI Gallery 之后（无 AI Gallery 时仍展示在导航末尾）
    updatedMenus.push({ ...navListConfig.gamewiki });
    return updatedMenus;
  }, [navMenuListLoaded, userInfo, menus, studioMenu, aiGalleryMenu]);

  const jumpToHome = () => {
    // history.push('/home');
    if (!logoJumpPermission) return;
    window.location.href = '/portal';
  };

  const {
    navList = menuList,
    options = defaultOptions,
    helpList: propsHelpList,
    logo = <img onClick={jumpToHome} src={getImg('logo')} alt="logo" className="lay-nav-logo" />,
  } = props;

  // global环境 funcom由于没有demo游戏,经分指引跳转帮助中心
  const helpList = useMemo(() => {
    let helpList: Array<any> = propsHelpList || [...defaultHelpList];
    const { organization = [] } = userInfo || {};
    if (IS_GLOBAL && organization.includes(ORGANIZATION_FUN)) {
      const findIndex = helpList.findIndex((item: { type?: string; }) => item?.type === 'guideList');
      if (findIndex > -1) {
        const guideList = helpList[findIndex];
        guideList.list = guideList.list.map((item: { eventName?: string; }) => {
          if (item?.eventName === 'dashboardGuideBox') {
            return {
              ...item,
              eventName: undefined,
            };
          }
          return item;
        });
        helpList = helpList.splice(findIndex, 1, guideList);
      }
    }
    return helpList;
  }, [propsHelpList, userInfo]);

  return (
    <NavMenu
      className="lay-nav"
      left={
        <>
          {/* logo 图标 */}
          <Logo>
            {/* 支持传入自定义LOGO */}
            {logo}
          </Logo>

          {/* 顶部导航 */}
          <NavList
            list={navList}
            defaultValue={defaultValue}
            isAIToolClicked={isAIToolClicked}
          ></NavList>
        </>
      }
      right={
        <>
          {/* 搜索组件 */}
          {ifHasSearch && searchPermission ? <Search options={options}></Search> : null}
          {/* Agent 首页入口 */}
          {isHaveAIPermission ? <Aigc /> : null}
          {/* 主题设置 */}
          <Theme />
          {/* 返回旧版按钮 */}
          {/* <OldVersion /> */}
          {/* AI 工具 */}
          {/* {isHaveAIPermission ? <AITools list={AI_LIST} isAIToolClicked={isAIToolClicked}></AITools> : null} */}
          {/* 用户反馈 */}
          {feedbackPermission && <Feedback></Feedback>}
          {/* 帮助弹窗 */}
          {helpPermission ? <Help list={helpList} defaultHelpUrl={props.defaultHelpUrl || defaultHelpUrl}></Help> : null}
          {/* 多语言切换 */}
          <Locale></Locale>
          {/* 登录展示组件 */}
          <Login userInfo={userInfo}></Login>
          {/** 添加历史版本自动弹窗 当前系统有权限才弹窗，没权限时虽然不展示菜单但可能手动链接跳转过来 */}
          {sysType === sysTypeOrEnvType.sys_type_dataBrain && <VersionAutoPopup />}
        </>
      }
    />
  );
}

export default connect((state: any) => {
  const { loading, home } = state;
  return {
    userInfo: home.userInfo,
    navMenuList: home.navMenuList,
    navMenuListLoaded: home.navMenuListLoaded,
    navStudioList: home.navStudioList,
    gameLoading: loading.effects['home/getUserInfo'],
  };
})(NavHeader);
